"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { Show, SignInButton, SignUpButton, UserButton, useAuth, useUser } from "@clerk/nextjs";
import { apiFetch } from "@/lib/api";
import { FEATURES, useAccess, type Role } from "@/lib/access";
import { consumeSse } from "@/lib/sse";
import { buildExportMarkdown, citationToSourceHit, safeFilename, svjtDeepLink } from "@/lib/present";
import CaseText from "@/components/CaseText";
import SavedList, { type SavedSummary } from "@/components/SavedList";
import CandidateList from "@/components/CandidateList";
import ApiKeysPanel from "@/components/ApiKeysPanel";
import AnswerView from "@/components/AnswerView";

interface Hit {
  title?: string | null;
  authors?: string[] | null;
  year?: number | null;
  issue?: string | null;
  landing_url?: string | null;
  kind?: string | null;
  heading?: string | null;
  page_start?: number | null;
  text?: string | null;
}

interface Source {
  hit: Hit;
  section_path?: string | null;
  outbound_refs?: { id: string; pincite?: string | null }[];
}

interface Citation {
  document_id: number;
  source_id: string;
  year: number | null;
  issue: string | null;
  title: string;
  authors: string[] | null;
  landing_url: string | null;
  pdf_url: string | null;
  publisher: string | null;
  src_kind: string;
  pincite: string | null;
  page_start: number | null;
  text: string;
}

const ROLE_LABELS: Record<Role, string> = {
  anonymous: "Gäst",
  user: "Standard",
  beta: "Beta",
  paid: "Pro",
  admin: "Admin",
};

const JOURNAL_NAMES: Record<string, string> = {
  svjt: "Svensk Juristtidning",
  jt: "Juridisk Tidskrift",
  ert: "Europarättslig tidskrift",
  fvr: "Förvaltningsrättslig tidskrift",
  juridikbok: "Juridikbok",
};

export default function HomePage() {
  const router = useRouter();
  const { isSignedIn } = useUser();
  const { getToken } = useAuth();
  // Role + feature access from the server matrix (GET /me). The same matrix
  // enforces access server-side, so the UI gates from one source of truth.
  const access = useAccess();
  const canSummarize = access.me ? access.has(FEATURES.caseSummary) : isSignedIn;
  const quota = access.usage;
  const quotaExhausted =
    quota !== null && quota.quota !== null && quota.remaining === 0 && !quota.use_byok;
  // Rows shown in the user dropdown menu (account type + monthly quota).
  const roleLabel = ROLE_LABELS[access.me?.role ?? "user"];
  const quotaRowLabel =
    quota === null
      ? "Månadsanslag: …"
      : quota.quota === null
        ? "Månadsanslag: obegränsat"
        : `Månadsanslag: ${quota.remaining} kvar`;
  const [refInput, setRefInput] = useState("");

  // View tabs above the search bar. Each tab is a snapshot tied to the case it
  // was opened for (Källor/Sammanfattning/Dom) or a standalone list
  // (Sparade/API-nycklar), so searching a new case opens new tabs without
  // emptying the old ones.
  type TabKind = "kallor" | "sammanfattning" | "dom" | "sparade" | "apinycklar";
  interface ViewTab {
    key: string;
    kind: TabKind;
    ref: string | null;
    /** Visible text next to the icon (case name, or the question for a saved one). */
    text: string;
    /** Full descriptive label for the title / aria-label. */
    label: string;
    question: string;
  }
  const [tabs, setTabs] = useState<ViewTab[]>([]);
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [canonicalRef, setCanonicalRef] = useState<string | null>(null);
  // Publication reference (e.g. "NJA 2014 s. 684") labels tabs for the case.
  const [caseDisplayRef, setCaseDisplayRef] = useState<string | null>(null);
  // Results of the pre-flight check (/check-lit-ref). The action buttons
  // appear only while the checked query matches the current input.
  const [checkedInput, setCheckedInput] = useState<string | null>(null);
  const [showKallor, setShowKallor] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const checksFresh = checkedInput !== null && checkedInput === refInput.trim();
  // The tab whose content is on screen (and whose data the toolbar acts on).
  const activeTab = tabs.find((t) => t.key === activeKey) ?? null;

  function openCaseTab(
    kind: "kallor" | "sammanfattning" | "dom",
    ref: string,
    refLabel: string,
    question: string,
  ) {
    const key = `${kind}:${ref}`;
    const word = kind === "kallor" ? "Källor" : "Sammanfattning";
    const label = kind === "dom" ? refLabel : `${word} - ${refLabel}`;
    setTabs((prev) =>
      prev.some((t) => t.key === key) ? prev : [...prev, { key, kind, ref, text: refLabel, label, question }],
    );
    setActiveKey(key);
  }
  function openStandaloneTab(kind: "sparade" | "apinycklar", label: string) {
    setTabs((prev) =>
      prev.some((t) => t.key === kind)
        ? prev
        : [...prev, { key: kind, kind, ref: null, text: label, label, question: "" }],
    );
    setActiveKey(kind);
  }
  function openSparadeTab() {
    openStandaloneTab("sparade", "Sparade sammanfattningar");
  }
  function openApiKeysTab() {
    openStandaloneTab("apinycklar", "API-nycklar");
  }
  function closeTab(key: string) {
    setTabs((prev) => prev.filter((t) => t.key !== key));
    // If the active tab was closed, fall back to the last remaining one.
    setActiveKey((cur) => {
      if (cur !== key) return cur;
      const remaining = tabs.filter((t) => t.key !== key);
      return remaining.length ? remaining[remaining.length - 1].key : null;
    });
  }

  // Horizontal tab scrolling: when the strip is wider than the search card,
  // ‹ › buttons page through the tabs.
  const tabsRef = useRef<HTMLDivElement>(null);
  const [tabOverflow, setTabOverflow] = useState(false);
  const [tabAtStart, setTabAtStart] = useState(true);
  const [tabAtEnd, setTabAtEnd] = useState(true);
  const updateTabNav = useCallback(() => {
    const el = tabsRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setTabOverflow(max > 1);
    setTabAtStart(el.scrollLeft <= 1);
    setTabAtEnd(el.scrollLeft >= max - 1);
  }, []);
  function scrollTabs(dir: 1 | -1) {
    tabsRef.current?.scrollBy({ left: dir * 200, behavior: "smooth" });
  }
  useEffect(() => {
    updateTabNav();
  }, [tabs, activeKey, updateTabNav]);
  useEffect(() => {
    window.addEventListener("resize", updateTabNav);
    return () => window.removeEventListener("resize", updateTabNav);
  }, [updateTabNav]);
  // Keep the active tab visible when it is opened or selected off-screen.
  useEffect(() => {
    const el = tabsRef.current;
    if (!el) return;
    const active = el.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!active) return;
    const elRect = el.getBoundingClientRect();
    const r = active.getBoundingClientRect();
    const delta = r.left < elRect.left ? r.left - elRect.left : r.right > elRect.right ? r.right - elRect.right : 0;
    if (delta) el.scrollBy({ left: delta, behavior: "smooth" });
  }, [activeKey]);
  /** The same glyphs as the "Visa källor" / "Sammanfatta" buttons: the tab
   * uses the icon instead of the word, followed by the case name. */
  function tabIcon(kind: TabKind) {
    if (kind === "dom") {
      return (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="m16 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z" />
          <path d="m2 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z" />
          <path d="M7 21h10" />
          <path d="M12 3v18" />
          <path d="M3 7h2c2 0 5-1 7-2 2 1 5 2 7 2h2" />
        </svg>
      );
    }
    if (kind === "kallor") {
      return (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <line x1="8" x2="21" y1="6" y2="6" />
          <line x1="8" x2="21" y1="12" y2="12" />
          <line x1="8" x2="21" y1="18" y2="18" />
          <line x1="3" x2="3.01" y1="6" y2="6" />
          <line x1="3" x2="3.01" y1="12" y2="12" />
          <line x1="3" x2="3.01" y1="18" y2="18" />
        </svg>
      );
    }
    if (kind === "sammanfattning") {
      return (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
          <path d="M20 3v4" />
          <path d="M22 5h-4" />
          <path d="M4 17v2" />
          <path d="M5 18H3" />
        </svg>
      );
    }
    if (kind === "sparade") {
      return (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
        </svg>
      );
    }
    if (kind === "apinycklar") {
      return (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
        </svg>
      );
    }
    return null;
  }

  // Per-tab results, keyed by tab key so each tab keeps its own content.
  const [citationsByKey, setCitationsByKey] = useState<Record<string, Citation[]>>({});
  const [citeBusy, setCiteBusy] = useState(false);
  const [citeError, setCiteError] = useState<string | null>(null);
  // The send button's pre-flight check (/check-lit-ref) in flight.
  const [checkBusy, setCheckBusy] = useState(false);

  // AI summaries per tab. Streaming writes into the running tab's entry.
interface Candidate {
  kind: string;
  ref_id: string;
  display: string;
  popular_name?: string | null;
  court_code?: string | null;
  decision_date?: string | null;
  title?: string | null;
  authors?: string[] | null;
  year?: number | null;
  label?: string | null;
  score: number;
  arms: string[];
  reasons: string[];
}
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [candidateBusy, setCandidateBusy] = useState(false);

  interface SummaryData {
    answer: string;
    sources: Source[];
    allSources: Citation[];
  }
  const [summariesByKey, setSummariesByKey] = useState<Record<string, SummaryData>>({});
  const [busy, setBusy] = useState(false);
  const [runningSummaryKey, setRunningSummaryKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Summary tools: save / download / saved-answers list.
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [docxBusy, setDocxBusy] = useState(false);
  const [hasSaved, setHasSaved] = useState(false);

  // Show the saved-summaries button in the header as soon as any exist.
  useEffect(() => {
    if (!isSignedIn) return;
    (async () => {
      try {
        const res = await apiFetch("/saved-answers", { token: (await getToken()) ?? undefined });
        if (!res.ok) return;
        const items = (await res.json()) as { id: number }[];
        setHasSaved(items.length > 0);
      } catch {
        /* header stays clean on failure */
      }
    })();
  }, [isSignedIn, getToken]);

  // Progress steps shown while the summary is being generated.
  const SUMMARY_STEPS = [
    "Tolkar rättsfallsreferensen…",
    "Hämtar citerande kontexter…",
    "Skickar till språkmodellen…",
    "Skriver sammanfattningen…",
  ];
  const [step, setStep] = useState(0);
  // "Tänker…" is shown while the model emits reasoning tokens before the first
  // content arrives — without this the UI would sit silent on step 3.
  const [thinking, setThinking] = useState(false);
  const stepTimers = useRef<NodeJS.Timeout[]>([]);

  /** 1) Public: pre-flight check (send button / Enter).
   *
   *  a) full judgment text fetchable → open the "Dom" tab (case identifier)
   *  b) literature sources cite the case → show the "Visa källor" button
   *  3) either check passed → show the "Sammanfatta" button
   */
  async function runChecks() {
    const q = refInput.trim();
    if (!q || checkBusy) return;
    setCheckBusy(true);
    setCiteError(null);
    // A new call replaces whatever is on screen — clear the other mode too.
    setError(null);
    // A different query than the previous check → refresh the case context
    // (buttons, refs). Tabs and their content stay open — persistence.
    if (checkedInput !== q) {
      setShowKallor(false);
      setShowSummary(false);
      setCanonicalRef(null);
      setCaseDisplayRef(null);
      setCandidates([]);  // Clear candidates when query changes
    }
    try {
      const pre = await apiFetch(`/check-lit-ref?q=${encodeURIComponent(q)}`);
      if (!pre.ok) {
        setCiteError(`Kunde inte tolka referensen (${pre.status}).`);
        return;
      }
      const preData = await pre.json();
      let canonicalRefSet: string | null = null;
      let displayRef: string | null = null;
      let hasText: boolean = false;
      let hasCites: boolean = false;
      let candidateResult: { found?: boolean; canonical?: boolean; display_ref?: string; count?: number; has_case_text?: boolean } | undefined;
      if (preData.found && preData.canonical) {
        // Normal successful check
        const canonical = preData.canonical as string;
        canonicalRefSet = canonical;
        displayRef = preData.display_ref || canonical;
        hasText = !!preData.has_case_text;
        hasCites = (preData.count ?? 0) > 0;
      } else if (pre.ok) {
        // HTTP ok but not found/canonical — try candidates
        candidateResult = preData;
      }
      
      if (!canonicalRefSet) {
        setShowKallor(false);
        setShowSummary(false);
        setCanonicalRef(null);
        setCaseDisplayRef(null);
      }
      
      // If we got a valid canonical ref, proceed normally
      if (canonicalRefSet && displayRef) {
        // (a) Full text fetchable → open the "Dom" tab for this case.
        if (hasText) openCaseTab("dom", canonicalRefSet as string, displayRef, q);
        // (b) + (3) Reveal the action buttons for the checks that passed.
        setShowKallor(hasCites);
        setShowSummary(hasText || hasCites);
        // Buttons are gated on checksFresh — mark this input as checked.
        setCheckedInput(q);
      } else if (candidateResult) {
        // No canonical ref but HTTP ok — try /candidates endpoint
        
        // Fetch candidates from /candidates endpoint
        const candRes = await apiFetch(`/candidates?q=${encodeURIComponent(q)}&kind=all&k=5`);
        if (!candRes.ok) {
          // Candidates API failed — keep citeError as-is (or show generic error)
          setCiteError(`Kunde inte hämta förslag (${candRes.status}).`);
        } else {
          const candData = await candRes.json();
          setCanonicalRef(null);
          // Render candidate list if we got at least one candidate
          if (candData.candidates && candData.candidates.length > 0) {
            setCandidates(candData.candidates as Candidate[]);
          } else {
            // No candidates found — show generic error instead of citeError message
            setCiteError("Ingen känd referens — försök t.ex. \u0022NJA 2014 s. 684\u0022.");
          }
        }
      } else {
        // Network error or other case — show generic error
        setCiteError(`Kunde inte tolka referensen (${pre.status}).`);
      }
    } catch (e) {
      // Network/CORS failures reject the fetch — surface them instead of
      // failing silently (an unhandled rejection here just cleared the
      // spinner and left the button looking dead).
      setCiteError(`Kunde inte nå API:et (${e instanceof Error ? e.message : String(e)}).`);
    } finally {
      setCheckBusy(false);
    }
  }

  /** 2) Public: list sources that cite the case (one observation per source).
   * The button is only visible after a successful check, so canonicalRef is
   * set by then. */
  async function runSources() {
    if (citeBusy || !canonicalRef) return;
    const ref = canonicalRef;
    const key = `kallor:${ref}`;
    setCiteBusy(true);
    setCiteError(null);
    setError(null);
    openCaseTab("kallor", ref, caseDisplayRef || ref, refInput.trim() || ref);
    try {
      const res = await apiFetch(`/cited-by?ref_id=${encodeURIComponent(ref)}&limit=100`);
      if (!res.ok) {
        setCiteError(`API-fel: ${res.status}`);
        return;
      }
      const data = await res.json();
      // One observation per source (document).
      const seen = new Set<number>();
      const unique: Citation[] = [];
      for (const c of data.citations ?? []) {
        if (seen.has(c.document_id)) continue;
        seen.add(c.document_id);
        unique.push(c);
      }
      setCitationsByKey((prev) => ({ ...prev, [key]: unique }));
    } catch (e) {
      // Network/CORS failures reject the fetch — surface them instead of
      // failing silently (an unhandled rejection here just cleared the
      // spinner and left the button looking dead).
      setCiteError(`Kunde inte nå API:et (${e instanceof Error ? e.message : String(e)}).`);
    } finally {
      setCiteBusy(false);
    }
  }

  /** 2) Logged-in only: AI summary, streamed via SSE with step progress. */
  async function runSummary() {
    if (!isSignedIn) {
      router.push("/sign-in");
      return;
    }
    if (access.me && !access.has(FEATURES.caseSummary)) {
      setError("Din kontotyp har inte tillgång till sammanfattning.");
      return;
    }
    if (!canonicalRef) return;
    const ref = canonicalRef;
    const key = `sammanfattning:${ref}`;
    const question = refInput.trim() || caseDisplayRef || ref;
    setBusy(true);
    setError(null);
    setCiteError(null);
    setStep(0);
    setThinking(false);
    setSummariesByKey((prev) => ({ ...prev, [key]: { answer: "", sources: [], allSources: [] } }));
    setRunningSummaryKey(key);
    // Generating a summary opens that case's "Sammanfattning" tab (active) so
    // the step progress and streamed answer are visible.
    openCaseTab("sammanfattning", ref, caseDisplayRef || ref, question);
    // Advance through the steps on a timed schedule — but NEVER claim the last
    // step ("Skriver sammanfattningen…") on a timer: that one is only true when
    // content actually starts flowing (onDelta), or "Tänker…" is shown while
    // the model emits reasoning. A blind timer is how "writing" stuck on
    // screen while the model was still retrieving or reasoning.
    const advance = () => setStep((s) => Math.min(s + 1, SUMMARY_STEPS.length - 2));
    for (let i = 0; i < SUMMARY_STEPS.length - 2; i++) {
      stepTimers.current.push(setTimeout(advance, (i + 1) * 8000));
    }
    try {
      const token = await getToken();
      const res = await apiFetch("/ask", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question, mode: "literature", k: 6, stream: true }),
        token: token ?? undefined,
      });
      if (res.status === 401) {
        // Session expired or the API rejected it — take the user to the real
        // sign-in route (Clerk), not the legacy /login (which does not exist).
        router.push("/sign-in");
        return;
      }
      if (res.status === 403) {
        // Server matrix says this role may not use the feature.
        setError("Din kontotyp har inte tillgång till sammanfattning.");
        return;
      }
      if (!res.ok || !res.body) {
        setError(`API-fel: ${res.status}`);
        return;
      }

      await consumeSse(res.body, {
        onSources: (srcs) => {
          setSummariesByKey((prev) => ({
            ...prev,
            [key]: { ...(prev[key] ?? { answer: "", sources: [], allSources: [] }), sources: srcs as Source[] },
          }));
          setStep(2); // kontexter klara → skriver
        },
        onAllSources: (srcs) => {
          setSummariesByKey((prev) => ({
            ...prev,
            [key]: { ...(prev[key] ?? { answer: "", sources: [], allSources: [] }), allSources: srcs as Citation[] },
          }));
        },
        onDelta: (text) => {
          setSummariesByKey((prev) => {
            const cur = prev[key] ?? { answer: "", sources: [], allSources: [] };
            return { ...prev, [key]: { ...cur, answer: cur.answer + text } };
          });
          setThinking(false);
          setStep(3); // text flödar
        },
        onReasoning: () => {
          // First reasoning token: the model is working but hasn't written
          // prose yet — show that, don't wait frozen on "Skriver…".
          setThinking(true);
          setStep(3);
        },
        onError: (msg) => setError(`Fel: ${msg}`),
      });
    } catch (e) {
      setError(`Kunde inte hämta svar: ${e instanceof Error ? e.message : e}`);
    } finally {
      stepTimers.current.forEach(clearTimeout);
      stepTimers.current = [];
      setBusy(false);
      setRunningSummaryKey(null);
      // A summary may have consumed quota (server-billed calls) — keep the
      // displayed "X kvar" accurate.
      void access.refreshUsage();
    }
  }

  /** 3) Logged-in: persist the active summary tab for later. */
  async function saveSummary() {
    if (!isSignedIn || saving || !activeTab || activeTab.kind !== "sammanfattning") return;
    const data = summariesByKey[activeTab.key];
    if (!data) return;
    setSaving(true);
    try {
      const res = await apiFetch("/saved-answers", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question: activeTab.question, mode: "literature", answer: data.answer, sources: data.sources }),
        token: (await getToken()) ?? undefined,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setHasSaved(true);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
    } catch (e) {
      setError(`Kunde inte spara sammanfattningen (${e instanceof Error ? e.message : e}).`);
    } finally {
      setSaving(false);
    }
  }

  /** 4) Download the active summary tab as a Word document. */
  async function downloadDocx() {
    if (docxBusy || !activeTab || activeTab.kind !== "sammanfattning") return;
    const data = summariesByKey[activeTab.key];
    if (!data) return;
    setDocxBusy(true);
    try {
      const markdown = buildExportMarkdown(
        data.answer,
        data.sources,
        data.allSources.map((c) => citationToSourceHit(c)),
      );
      const question = activeTab.question || "Svar";
      const res = await apiFetch("/export/docx", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question, markdown, mode: "literature" }),
        token: (await getToken()) ?? undefined,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rattskallor-${safeFilename(question)}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(`Kunde inte generera Word-filen (${e instanceof Error ? e.message : e}).`);
    } finally {
      setDocxBusy(false);
    }
  }

  /** 5) Open a saved summary as its own (case-less) Sammanfattning tab. */
  function loadSaved(s: SavedSummary) {
    const key = `sammanfattning:saved:${s.id}`;
    const label = `Sammanfattning - ${s.question}`;
    setTabs((prev) =>
      prev.some((t) => t.key === key)
        ? prev
        : [...prev, { key, kind: "sammanfattning", ref: null, text: s.question, label, question: s.question }],
    );
    setSummariesByKey((prev) => ({
      ...prev,
      [key]: { answer: s.answer, sources: s.sources as unknown as Source[], allSources: [] },
    }));
    setActiveKey(key);
    setRefInput(s.question);
    setCiteError(null);
    setError(null);
  }

  /** Full citation: Författare, Titel, Tidskrift År nr X s. Y */
  function citationLine(c: Citation): string {
    const parts: string[] = [];
    if (c.authors?.length) parts.push(c.authors.join(", "));
    if (c.title) parts.push(c.title);
    const journal = JOURNAL_NAMES[c.source_id] ?? c.source_id;
    // issue already carries the year ("2016 nr 9"); don't duplicate it.
    const issueStartsWithYear = c.issue && c.year && c.issue.startsWith(String(c.year));
    if (c.source_id === "svjt") {
      // SvJT URLs are one page per printed page — the article's start page is
      // the trailing number in the landing URL. Footnote nodes lack page_start,
      // so this is what turns "SvJT 2025 nr 2" into "… s. 184".
      const m = (c.landing_url || "").match(/\/(\d+)$/);
      const startPage = m ? parseInt(m[1], 10) : null;
      let where = "";
      if (c.src_kind === "footnote" && c.pincite) where = `, fn ${c.pincite}`;
      else if (c.page_start && (!startPage || c.page_start !== startPage)) where = `, s. ${c.page_start}`;
      const loc = [journal, c.year && !issueStartsWithYear ? String(c.year) : null, c.issue]
        .filter(Boolean)
        .join(" ");
      const tail = [loc, startPage ? `s. ${startPage}` : null].filter(Boolean).join(" ") + (where || "");
      if (tail) parts.push(tail);
      return parts.join(", ");
    }
    // Books: "Författare, Titel, Förlag, År, s. X" (no journal).
    if (c.source_id === "juridikbok" && c.publisher) {
      const loc = [c.publisher, c.year ? String(c.year) : null].filter(Boolean).join(", ");
      const tail = [loc, c.page_start ? `s. ${c.page_start}` : null].filter(Boolean).join(", ");
      if (loc) parts.push(tail);
      return parts.join(", ");
    }
    const page = c.pincite?.replace(/^p\.\s*/i, "") ?? (c.page_start ? String(c.page_start) : null);
    const loc = [journal, c.year && !issueStartsWithYear ? String(c.year) : null, c.issue, page ? `s. ${page}` : null]
      .filter(Boolean)
      .join(" ");
    if (loc) parts.push(loc);
    return parts.join(", ");
  }

  const activeSummary = activeTab ? summariesByKey[activeTab.key] : undefined;

  return (
    <>
      <header className="site-header">
        <div className="inner">
          <Link href="/" className="wordmark">
            Rättskällor<span className="dot">.</span>se
          </Link>
          <div className="nav-actions">
            <Show when="signed-out">
              <SignInButton mode="modal" fallbackRedirectUrl="/">
                <button type="button" className="btn btn-outline">Logga in</button>
              </SignInButton>
              <SignUpButton mode="modal" fallbackRedirectUrl="/">
                <button type="button" className="btn">Skapa konto</button>
              </SignUpButton>
            </Show>
            <Show when="signed-in">
              <UserButton>
                <UserButton.MenuItems>
                  {hasSaved && (
                    <UserButton.Action
                      label="Sparade sammanfattningar"
                      labelIcon={
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                        </svg>
                      }
                      onClick={() => openSparadeTab()}
                    />
                  )}
                  <UserButton.Action
                    label={`Kontotyp: ${roleLabel}`}
                    labelIcon={
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                      </svg>
                    }
                    onClick={() => void access.refreshUsage()}
                  />
                  <UserButton.Action
                    label={quotaRowLabel}
                    labelIcon={
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <circle cx="12" cy="12" r="9" />
                        <path d="M12 7v5l3 3" />
                      </svg>
                    }
                    onClick={() => void access.refreshUsage()}
                  />
                  {access.has(FEATURES.accountKeys) && (
                    <UserButton.Action
                      label="API-nycklar"
                      labelIcon={
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
                        </svg>
                      }
                      onClick={() => openApiKeysTab()}
                    />
                  )}
                </UserButton.MenuItems>
              </UserButton>
            </Show>
          </div>
        </div>
      </header>

      <div className="container">
        <div className="view-tabs-slot">
          {tabs.length > 0 && (
            <div className="view-tabs-bar">
              {tabOverflow && (
                <button
                  type="button"
                  className="btn-bare tab-nav"
                  aria-label="Visa tidigare flikar"
                  title="Visa tidigare flikar"
                  disabled={tabAtStart}
                  onClick={() => scrollTabs(-1)}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                </button>
              )}
              <div className="view-tabs" role="tablist" aria-label="Vyer" ref={tabsRef} onScroll={updateTabNav}>
                {tabs.map((tab) => (
                  <div
                    key={tab.key}
                    role="tab"
                    aria-selected={activeKey === tab.key}
                    aria-label={tab.label}
                    className={`view-tab${activeKey === tab.key ? " active" : ""}`}
                    title={tab.label}
                    onClick={() => setActiveKey(tab.key)}
                  >
                    <span className="tab-label">
                      {tabIcon(tab.kind)}
                      <span className="tab-text">{tab.text}</span>
                    </span>
                    <button
                      type="button"
                      className="btn-bare tab-close"
                      aria-label={`Stäng ${tab.label}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        closeTab(tab.key);
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
              {tabOverflow && (
                <button
                  type="button"
                  className="btn-bare tab-nav"
                  aria-label="Visa senare flikar"
                  title="Visa senare flikar"
                  disabled={tabAtEnd}
                  onClick={() => scrollTabs(1)}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
              )}
            </div>
          )}
        </div>
        <section className="card query-card">
          <h2>Rättsfall</h2>
          <div className="query-input-row">
            <input
              type="text"
              value={refInput}
              onChange={(e) => setRefInput(e.target.value)}
              placeholder="t.ex. NJA 2014 s. 684, Divisionschefen eller HFD 2016 ref. 37"
              onKeyDown={(e) => e.key === "Enter" && runChecks()}
            />
            <button
              className={`btn btn-icon${checkBusy ? " busy" : ""}`}
              type="button"
              onClick={runChecks}
              disabled={checkBusy || !refInput.trim()}
              aria-label="Sök"
              title="Sök"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="22" x2="11" y1="2" y2="13" />
                <path d="M22 2 15 22 11 13 2 9l20-7z" />
              </svg>
            </button>
            {checksFresh && showKallor && (
              <button
                className={`btn btn-outline btn-icon${citeBusy ? " busy" : ""}`}
                type="button"
                onClick={runSources}
                disabled={citeBusy}
                aria-label="Visa källor"
                title="Visa källor"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="8" x2="21" y1="6" y2="6" />
                  <line x1="8" x2="21" y1="12" y2="12" />
                  <line x1="8" x2="21" y1="18" y2="18" />
                  <line x1="3" x2="3.01" y1="6" y2="6" />
                  <line x1="3" x2="3.01" y1="12" y2="12" />
                  <line x1="3" x2="3.01" y1="18" y2="18" />
                </svg>
              </button>
            )}
            {checksFresh && showSummary && (
              <button
                className={`btn btn-outline btn-icon${busy ? " busy" : ""}`}
                type="button"
                onClick={runSummary}
                disabled={busy || !canSummarize}
                aria-label="Sammanfatta"
                title={
                  !isSignedIn
                    ? "Sammanfattning kräver att du är inloggad."
                    : !canSummarize
                      ? "Din kontotyp har inte tillgång till sammanfattning."
                      : quotaExhausted
                        ? "Månadsanslaget är slut denna månad."
                        : "Sammanfatta"
                }
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
                  <path d="M20 3v4" />
                  <path d="M22 5h-4" />
                  <path d="M4 17v2" />
                  <path d="M5 18H3" />
                </svg>
              </button>
            )}
          </div>
          {candidates.length > 0 && (
            <CandidateList q={refInput.trim()} candidates={candidates} onSelect={(c) => {
              setRefInput(c.display);
              runChecks();
            }} />
          )}
          {citeError && <div className="callout callout--boxed callout--warn error-box">{citeError}</div>}
          {error && <div className="callout callout--boxed callout--warn error-box">{error}</div>}
        </section>

        {activeTab && (
          <div className="view-panel">
            <h2 className="view-panel-title">{activeTab.label}</h2>
            {activeTab.kind === "sparade" ? (
              <SavedList onLoad={loadSaved} onChanged={setHasSaved} />
            ) : activeTab.kind === "apinycklar" ? (
              <ApiKeysPanel />
            ) : activeTab.kind === "dom" && activeTab.ref ? (
              <CaseText
                refId={activeTab.ref}
                getToken={getToken}
                onOpenCase={(ref, label) => openCaseTab("dom", ref, label, label)}
              />
            ) : activeTab.kind === "kallor" ? (
          citationsByKey[activeTab.key] ? (
            <div className="card-grid">
              {citationsByKey[activeTab.key].map((c) => {
                const href = svjtDeepLink(c.landing_url, c.source_id, c.page_start, c.pincite) ?? c.pdf_url;
                return (
                  <div className="card card--sm card--flat source-item cite-card" key={c.document_id}>
                    <div className="cite-line">{citationLine(c)}</div>
                    {href && (
                      <div className="meta">
                        <a href={href} target="_blank" rel="noreferrer">Öppen källa ↗</a>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="empty">Inga källor ännu — tryck på “Visa källor”.</p>
          )
        ) : activeTab.kind === "sammanfattning" ? (
          <>
            {busy && runningSummaryKey === activeTab.key && (
              <div className="card card--flat progress" role="status" aria-live="polite">
                <ol className="progress-steps">
                  {SUMMARY_STEPS.map((label, i) => {
                    let shown = label;
                    if (i === 3 && thinking) shown = "Tänker…";
                    return (
                      <li key={i} className={i < step ? "done" : i === step ? "active" : ""}>
                        <span className="step-dot" />
                        {shown}
                      </li>
                    );
                  })}
                </ol>
              </div>
            )}

            {activeSummary?.answer && (
              <div className="answer-wrap">
                <AnswerView
                  answer={activeSummary.answer}
                  sources={activeSummary.sources}
                  allSources={activeSummary.allSources}
                  footer={
                    !busy ? (
                      <div className="answer-toolbar">
                        {isSignedIn && (
                          <button className="btn btn-outline btn-sm" onClick={saveSummary} disabled={saving}>
                            {saving ? "Sparar…" : savedFlash ? "Sparad ✓" : "Spara sammanfattning"}
                          </button>
                        )}
                        <button className="btn btn-outline btn-sm" onClick={downloadDocx} disabled={docxBusy}>
                          {docxBusy ? "Genererar…" : "Ladda ner Word (.docx)"}
                        </button>
                      </div>
                    ) : undefined
                  }
                />
              </div>
            )}
          </>
            ) : null}
          </div>
        )}

      </div>
    </>
  );
}
